import * as GTMEventTracker from 'vendor/google/gtm/gtmEventTracker';
import { createLoader, removeLoader } from 'helpers/loader/loader';
import axios from 'axios';
import { toggleDisplay, toggleCssClass } from 'helpers/toggle/toggle';
import scrollToElement from 'helpers/scroll/scrollTo';
import { Modal } from 'components/modal/modal';

export const GuidedSelling = (() => {
    let addedEventListeners = false;
    const targetNode = document.getElementById('ajax-content');

    // Create an observer instance
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(() => {
            init();
        });
    });

    // Configuration of the observer
    const config = { childList: true };

    // Start observing the target node for configured mutations
    observer.observe(targetNode, config);

    let _config = {
        selectors: {},
        // template that gets copied to 'data' on init
        dataTemplate: {
            questions: [], // stores questions we receive from the API
            answers: [], // stores answers given by user (within this script)
            filters: null, // stores string we use to redirect to the filtered page, received from API,
            answerHistory: [] // used to be able to select given answer when visitor goes back a question
        },
        // data receives keys from dataTemplate (for reset purposes)
        data: {},
        currentQuestionType: 'single',
        currentQuestionId: null, // stores id from question that has 'current: true' in API response
        currentQuestionNr: 0, // stores index from current question in data->questions array
        currentQuestionProductCount: 0,
        inactiveQuestionsCount: 0, // counter used to store the amount of 'current: false' questions from API response
        templates: {
            question: function () {
                return _config.selectors.container.querySelector('#question-template').content;
            },
            answerSingle: function () {
                return _config.selectors.container.querySelector('#answer-single-template').content;
            },
            answerMultiple: function () {
                return _config.selectors.container.querySelector('#answer-multiple-template').content;
            },
            answerSingleImage: function () {
                return _config.selectors.container.querySelector('#answer-single-image-template').content;
            },
            answerMultipleImage: function () {
                return _config.selectors.container.querySelector('#answer-multi-image-template').content;
            }
        },
        apiUrl: null, // loaded from data-attribute in template
        isActive: false,
        switchToColumns: 15,
        modal: new Modal({
            selector: '.guided-selling-modal',
            isClosable: true
        })
    };

    const _resetConfigSelectors = () => {
        _config = {
            ..._config,
            selectors: {
                guidedSellingOpeners: document.querySelectorAll('.show-guided-selling'),
                nrOfResults: document.querySelector('.btn-nr-of-results'),
                container: document.querySelector('.guided-selling-container'),
                actionbar: '.action-bar',
                contentContainer: '.guided-selling-content',
                question: '.guided-selling-content .question',
                answer: '.guided-selling-content .answer',
                progressbar: '.progress-bar',
                progressbarCurrentQuestion: '#currentQuestion',
                progressbarTotalQuestions: '#totalQuestions',
                buttons: {
                    next: '.action-bar .btn-next',
                    previous: '.action-bar .btn-previous',
                    close: '.guided-selling-header .btn-close'
                },
                actionMessages: '.action-bar-messages'
            },
            templates: {
                question: function () {
                    return _config.selectors.container.querySelector('#question-template').content;
                },
                answerSingle: function () {
                    return _config.selectors.container.querySelector('#answer-single-template').content;
                },
                answerMultiple: function () {
                    return _config.selectors.container.querySelector('#answer-multiple-template').content;
                },
                answerSingleImage: function () {
                    return _config.selectors.container.querySelector('#answer-single-image-template').content;
                },
                answerMultipleImage: function () {
                    return _config.selectors.container.querySelector('#answer-multi-image-template').content;
                }
            },
            modal: new Modal({
                selector: '.guided-selling-modal',
                isClosable: true
            })
        };
    };

    const init = () => {
        _resetConfigSelectors();
        if (!_config.selectors.container) {
            return;
        }

        // copy dataTemplate to data
        _config.data = Object.assign({}, _config.dataTemplate);

        if (_config.selectors.guidedSellingOpeners) {
            // Add eventListeners
            _addEventListeners();

            for (let i = 0; i < _config.selectors.guidedSellingOpeners.length; i++) {
                // Handle click on open button
                _config.selectors.guidedSellingOpeners[i].addEventListener(
                    'click',
                    // eslint-disable-next-line no-loop-func
                    (event) => {
                        _removeQuestion(_config.currentQuestionId);
                        event.preventDefault();
                        scrollToElement(_config.selectors.container);
                        GTMEventTracker.trackEvent('Subcategory', 'Choice aid', 'Open');
                        if (_config.isActive) {
                            _resetHash();
                            _reset();
                            hide();
                        } else {
                            _fetchData();
                        }
                    }
                );

                // Set API URL
                _config.apiUrl = _config.selectors.guidedSellingOpeners[0].dataset.url;
            }
        }
    };

    const _addEventListeners = () => {
        if (addedEventListeners) {
            return;
        }

        // Listen to URL change to open guided selling from other components
        window.addEventListener('hashchange', () => {
            // #open-kh is used in content-blocks urls
            if (location.hash === '#open-kh') {
                // trigger click on 'open' button
                _config.selectors.guidedSellingOpener.click();
            }
        });

        if (localStorage.getItem('showGuidedSelling') === 'true') {
            // Small delay before opening guided selling
            setTimeout(() => {
                // trigger click on 'open' button
                _config.selectors.guidedSellingOpener.click();
                localStorage.removeItem('showGuidedSelling');
            }, 100);
        }

        _config.selectors.container.addEventListener('click', (event) => {
            const infoIcon = event.target.closest('.open-info-popup');
            const answer = event.target.closest(_config.selectors.answer);
            const btnNext = event.target.closest(_config.selectors.buttons.next);
            const btnPrevious = event.target.closest(_config.selectors.buttons.previous);
            const btnClose = event.target.closest(_config.selectors.buttons.close);
            const NrofResultsLink = event.target.closest('.btn-nr-of-results');

            if (infoIcon) {
                _handleInfoIcon(event);
                return; // hack to prevent the answer from being clicked while clicking on the icon.
            }

            if (answer) {
                _handleAnswer(event);
            }
            if (btnNext) {
                _handleNextButton(event);
            }
            if (btnPrevious) {
                _handlePreviousButton(event);
            }
            if (btnClose) {
                GTMEventTracker.trackEvent('Subcategory', 'Choice aid', 'Close');
                _reset();
                hide();
            }
            if (NrofResultsLink) {
                _handleNrofResultsLink(event);
            }
        });

        addedEventListeners = true;
    };

    const _fetchData = (buttonType) => {
        // add loader to container
        createLoader(_config.selectors.container);

        // buttonType checks if it was the 'previous' button that called _fetchData
        // if it was the 'previous' button, never go to _redirectToFilteredPage()
        // even if it's the last question we're on
        if (!_isLastQuestion() || buttonType === 'previous') {
            // delete all old data
            _config.data.questions = [];
            show();
            _getQuestions(() => {
                _setGlobalData();
                scrollToElement(_config.selectors.container);
                // remove loader from container
                removeLoader(_config.selectors.container);
            });
        } else {
            _getQuestions(() => {
                _redirectToFilteredPage();
                // remove loader from container
                removeLoader(_config.selectors.container);
            });
        }
    };

    const show = () => {
        _config.isActive = true;
        _config.selectors.container.classList.remove('d-none');
        _config.selectors.container.classList.add('d-flex');
    };

    const hide = () => {
        _config.isActive = false;
        _config.selectors.container.classList.add('d-none');
        _config.selectors.container.classList.remove('d-flex');
    };

    const _reset = () => {
        _config.selectors.container.querySelector(_config.selectors.contentContainer).innerHTML = '';
        // reset data
        _config.data = {};
        _config.data = Object.assign({}, _config.dataTemplate);
    };

    const _resetHash = () => {
        const urlObject = new URL(document.URL);
        urlObject.hash = '#';
        document.location.href = urlObject.href;
    };

    const _getQuestions = (callback) => {
        const postdata = {};
        _config.data.answers.forEach((item) => {
            postdata[`qs${item.question}`] = item.answerId;
        });
        axios
            .post(_config.apiUrl, postdata)
            .then(({ data }) => {
                if (data.success) {
                    _saveQuestions(data.payload.questions);
                    _config.currentQuestionProductCount = data.payload.productCount;
                    _setNrOfResults(data.payload.productCount);
                    _config.data.filters = data.payload.filters;
                    // if the inactive questions count equals the total length of all questions
                    // this means there are no active (current) questions anymore in the API response
                    // redirect to filtered page instead of rendering question
                    if (_config.inactiveQuestionsCount === _config.data.questions.length) {
                        _redirectToFilteredPage();
                    } else {
                        _renderQuestion(_config.currentQuestionNr);
                        callback();
                    }

                    const btnPrevious = _config.selectors.container.querySelector(_config.selectors.buttons.previous);
                    if (_isFirstQuestion()) {
                        toggleDisplay({
                            isVisible: false,
                            element: btnPrevious
                        });
                        _config.selectors.container
                            .querySelector(_config.selectors.actionbar)
                            .classList.remove('justify-content-between');
                        _config.selectors.container
                            .querySelector(_config.selectors.actionbar)
                            .classList.add('justify-content-end');
                    } else {
                        toggleDisplay({
                            isVisible: true,
                            element: btnPrevious
                        });
                        _config.selectors.container
                            .querySelector(_config.selectors.actionbar)
                            .classList.add('justify-content-between');
                        _config.selectors.container
                            .querySelector(_config.selectors.actionbar)
                            .classList.remove('justify-content-end');
                    }
                }
            })
            .catch(function (error) {
                console.error('GuidedSelling: failed to fetch questions from', _config.apiUrl, error);
            });
    };

    /* Fetch and handle data from the server */
    const _saveQuestions = (questions) => {
        // reset inactiveQuestionsCount
        _config.inactiveQuestionsCount = 0;

        questions.forEach((question, index) => {
            // if there is a current question, save it to the config object
            if (question.current) {
                _config.currentQuestionId = question.questionid;
                _config.currentQuestionNr = index;
            } else {
                // add 1 to inactive questions count
                _config.inactiveQuestionsCount++;
            }

            _config.data.questions.push(question);
        });
        _setNextButtonText();
    };

    /* Guided selling component logic */

    const _renderQuestion = (nr) => {
        const question = _config.data.questions[nr];

        const isMultiSelect = question.ismultiselect;
        if (isMultiSelect) {
            _config.currentQuestionType = 'multiselect';
        } else {
            _config.currentQuestionType = 'single';
        }

        switch (_config.currentQuestionType) {
            case 'single':
                _renderSingleSelectQuestion(question);
                break;

            case 'multiselect':
                _renderMultiSelectQuestion(question);
                break;

            default:
                _renderSingleSelectQuestion(question);
        }
    };

    const _renderSingleSelectQuestion = (question) => {
        const contentContainer = _config.selectors.container.querySelector(_config.selectors.contentContainer);
        const questionTemplate = _config.templates.question().cloneNode(true);

        questionTemplate.querySelector('.question').dataset.id = question.questionid;
        questionTemplate.querySelector('.title').innerText = question.question;
        questionTemplate.querySelector('.subtitle').innerText = question.explanation;

        _setAnswerType(questionTemplate, question);

        // get answers
        question.answers.forEach((answer) => {
            const answerHtml = _renderAnswer(answer, false);
            // append answer in the question template
            questionTemplate.querySelector('.answers-container').appendChild(answerHtml);
        });

        // append the question to the guided selling container
        contentContainer.appendChild(questionTemplate);

        // find out if we already have an answer for this question
        const givenAnswer = _config.data.answerHistory.find(
            (answer) => parseInt(answer.question) === parseInt(question.questionid)
        );

        if (givenAnswer) {
            // this is the earlier given answer, activate this answer
            const answer = contentContainer.querySelector(`.answer[data-id="${givenAnswer.answerId}"]`);
            _setAnswerActive(answer);
        }
    };

    const _renderMultiSelectQuestion = (question) => {
        const contentContainer = _config.selectors.container.querySelector(_config.selectors.contentContainer);
        const questionTemplate = _config.templates.question().cloneNode(true);

        questionTemplate.querySelector('.question').dataset.id = question.questionid;
        questionTemplate.querySelector('.title').innerText = question.question;
        questionTemplate.querySelector('.subtitle').innerText = question.explanation;

        _setAnswerType(questionTemplate, question);

        // get answers
        question.answers.forEach((answer) => {
            const answerHtml = _renderAnswer(answer, true);
            // append answer in the question template
            questionTemplate.querySelector('.answers-container').appendChild(answerHtml);
        });

        // append the question to the guided selling container
        contentContainer.appendChild(questionTemplate);

        // find out if we already have an answer for this question
        const givenAnswer = _config.data.answerHistory.find(
            (answer) => parseInt(answer.question) === parseInt(question.questionid)
        );
        if (givenAnswer) {
            givenAnswer.answerId.split(',').forEach((item) => {
                const answer = contentContainer.querySelector(`.answer[data-id="${item}"]`);
                _setAnswerActive(answer);
            });
        }
    };

    const _setAnswerType = (questionTemplate, question) => {
        questionTemplate.querySelector('.answers-container').classList.add('justify-content-md-center', 'layout-rows');
        questionTemplate
            .querySelector('.answers-container')
            .classList.remove('justify-content-md-left', 'layout-columns');
        if (question.answers.length >= _config.switchToColumns) {
            questionTemplate
                .querySelector('.answers-container')
                .classList.remove('justify-content-md-center', 'layout-rows');
            questionTemplate
                .querySelector('.answers-container')
                .classList.add('justify-content-md-left', 'layout-columns');
        }
    };

    const _removeQuestion = (nr) => {
        const question = _config.selectors.container.querySelector(`${_config.selectors.question}[data-id='${nr}']`);
        if (question) {
            question.parentNode.removeChild(question);
        }
    };

    const _renderAnswer = (data, isMultiSelect) => {
        let answerTemplate;

        if (isMultiSelect) {
            // get multiselect template
            answerTemplate = data.imageurl
                ? _config.templates.answerMultipleImage().cloneNode(true)
                : _config.templates.answerMultiple().cloneNode(true);
        } else {
            // get single select template
            answerTemplate = data.imageurl
                ? _config.templates.answerSingleImage().cloneNode(true)
                : _config.templates.answerSingle().cloneNode(true);
        }
        answerTemplate.querySelector('.title').innerText = data.answer;
        answerTemplate.querySelector('.answer').dataset.productCount = data.count;
        answerTemplate.querySelector('.answer').dataset.id = data.answerid;
        answerTemplate.querySelector('.answer').dataset.value = data.answer;
        answerTemplate.querySelector('.answer').dataset.caption = data.caption || data.answer;
        answerTemplate.querySelector('.answer').dataset.summary = data.summary;

        const input = answerTemplate.querySelector('.form-check-input');
        const label = answerTemplate.querySelector('.form-check-label');
        const inputId = `guided-selling-answer-${data.answerid}`;
        input.id = inputId;
        answerTemplate.querySelector('.answer').dataset.testId = inputId;
        label.htmlFor = inputId;
        label.setAttribute('aria-label', data.answer);

        if (data.imageurl) {
            answerTemplate.querySelector('img').src = data.imageurl;
        }

        if (!data.summary) {
            answerTemplate.querySelector('.open-info-popup').classList.add('d-none');
        }

        return answerTemplate;
    };

    const _setGlobalData = () => {
        const progressbar = _config.selectors.container.querySelector(_config.selectors.progressbar);
        const questionNr = _config.currentQuestionNr + 1;
        const percentage = Math.ceil((questionNr / _config.data.questions.length) * 100);

        progressbar.style.width = `${percentage}%`;
        progressbar.querySelector(_config.selectors.progressbarTotalQuestions).innerText =
            _config.data.questions.length;
        progressbar.querySelector(_config.selectors.progressbarCurrentQuestion).innerText = questionNr;
    };

    const _handleNextButton = (event) => {
        event.preventDefault();

        // if current question is not answered, show message
        if (!_isCurrentQuestionAnswered()) {
            toggleDisplay({
                element: document.querySelector(_config.selectors.actionMessages),
                isVisible: true
            });
            return;
        }
        _config.selectors.nrOfResults.classList.remove('d-none');
        _removeQuestion(_config.currentQuestionId);
        _fetchData();
    };

    const _handlePreviousButton = (event) => {
        event.preventDefault();

        // Remove validation message
        toggleDisplay({
            element: document.querySelector(_config.selectors.actionMessages),
            isVisible: false
        });

        _removeQuestion(_config.currentQuestionId);
        // @TODO: Maybe refactor this to removeAnswer()?
        if (_isCurrentQuestionAnswered()) {
            _removeCurrentAnswer();
        }
        _removeLastAnswer();
        _fetchData('previous'); // add buttonType so _fetchData can identify which button triggered it
    };

    const _handleAnswer = (event) => {
        event.preventDefault();
        // Remove validation message
        toggleDisplay({
            element: document.querySelector(_config.selectors.actionMessages),
            isVisible: false
        });
        const answer = event.target.closest(_config.selectors.answer);
        _setAnswerActive(answer);
    };

    const _setAnswerActive = (answer) => {
        const question = answer.closest(_config.selectors.question);

        if (_config.currentQuestionType === 'single') {
            _setNrOfResults(answer.dataset.productCount);
            // make the single answer active and add the data to the data
            _config.selectors.container.querySelectorAll(_config.selectors.answer).forEach((answer) => {
                answer.classList.remove('active');
                answer.querySelector('input').checked = false;
                _config.selectors.nrOfResults.classList.remove('d-none');
            });
            answer.classList.add('active');
            answer.querySelector('input').checked = true;
            _config.selectors.nrOfResults.classList.add('d-none');
            _addAnswerToData({
                question: question.dataset.id,
                answerId: answer.dataset.id,
                answer: answer.dataset.value
            });
        } else if (_config.currentQuestionType === 'multiselect') {
            let productCount = 0;
            toggleCssClass(answer, 'active');
            answer.querySelector('input').checked = !answer.querySelector('input').checked;

            const allAnswers = _config.selectors.container.querySelectorAll(
                `${_config.selectors.contentContainer} .answer.active`
            );
            let answerList = '';

            if (allAnswers.length > 0) {
                // answers given, add them to the answer data
                allAnswers.forEach((a) => {
                    productCount += parseInt(a.dataset.productCount);
                    if (answerList === '') {
                        answerList += a.dataset.id;
                    } else {
                        answerList += `,${a.dataset.id}`;
                    }
                });
                _config.selectors.nrOfResults.classList.add('d-none');

                _setNrOfResults(productCount);

                _addAnswerToData({
                    question: question.dataset.id,
                    answerId: answerList,
                    answer: ''
                });
            } else {
                // no answer given, remove the whole question from the answerdata
                _removeAnswerFromData(question.dataset.id);
                _setNrOfResults(_config.currentQuestionProductCount);
                _config.selectors.nrOfResults.classList.remove('d-none');
            }
        }
    };

    const _addAnswerToData = (data) => {
        _config.data.answers = _config.data.answers.filter((answer) => {
            return answer.question !== data.question;
        });

        _config.data.answers.push({
            question: data.question,
            answerId: data.answerId,
            answer: data.answer
        });

        // copy answers object to answerhistory object
        _config.data.answerHistory = JSON.parse(JSON.stringify(_config.data.answers));
    };

    const _removeAnswerFromData = (question) => {
        _config.data.answers = _config.data.answers.filter((answer) => {
            return answer.question !== question;
        });
    };

    const _handleInfoIcon = (event) => {
        event.stopPropagation();
        event.preventDefault();
        const answer = event.target.closest(_config.selectors.answer);

        const modal = document.querySelector(_config.modal.config.selector);
        modal.querySelector('#guided-selling-modal-title').innerHTML = answer.dataset.caption;
        modal.querySelector('.modal-content div').innerHTML = answer.dataset.summary;
        _config.modal.show();
    };

    const _isFirstQuestion = () => {
        if (_config.data.questions.length === 0) {
            return false;
        }
        const index = _config.data.questions.findIndex((question) => {
            return question.questionid === _config.currentQuestionId;
        });

        return index === 0;
    };

    const _isLastQuestion = () => {
        if (_config.data.questions.length === 0) {
            return false;
        }

        const index = _config.data.questions.findIndex((question) => {
            return question.questionid === _config.currentQuestionId;
        });
        return index + 1 === _config.data.questions.length;
    };

    const _redirectToFilteredPage = () => {
        GTMEventTracker.trackEvent('Subcategory', 'Choice aid', 'Show results');
        const baseUrl = window.location.origin + window.location.pathname;
        window.location = `${baseUrl}#${_config.data.filters}`;
        _reset();
        hide();
    };

    const _removeLastAnswer = () => {
        // remove last answer from _config.data.answers array
        _config.data.answers.pop();
    };

    const _removeCurrentAnswer = () => {
        // get index from current answer in answers array
        const currentAnswerIndex = _config.data.answers.findIndex(
            (answer) => answer.question === _config.currentQuestionId.toString()
        );
        // if there is an answer that matches the current question, remove it from the array
        if (currentAnswerIndex > -1) {
            _config.data.answers.splice(currentAnswerIndex, 1);
        }
    };

    const _isCurrentQuestionAnswered = () => {
        const index = _config.data.answers.findIndex(
            (answer) => answer.question === _config.currentQuestionId.toString()
        );
        return index > -1;
    };

    const _setNextButtonText = () => {
        const nextText = _config.selectors.container
            .querySelector(_config.selectors.buttons.next)
            .querySelector('.next-text');

        const resultsText = _config.selectors.container
            .querySelector(_config.selectors.buttons.next)
            .querySelector('.show-results-text');

        if (!_isLastQuestion()) {
            toggleDisplay({ isVisible: true, element: nextText });
            toggleDisplay({ isVisible: false, element: resultsText });
        } else {
            toggleDisplay({ isVisible: false, element: nextText });
            toggleDisplay({ isVisible: true, element: resultsText });
        }
    };

    const _setNrOfResults = (nr) => {
        const el = _config.selectors.container.querySelector('.btn-nr-of-results span');
        el.innerText = nr;
    };

    const _handleNrofResultsLink = (event) => {
        event.preventDefault();
        _redirectToFilteredPage();
    };

    return {
        init: init,
        show: show,
        hide: hide
    };
})();
